import React from 'react'
import styled from '@emotion/styled'
import { IconButton, Typography, useTheme } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { getColorForDarkMode } from 'utils'
import { getTextWithLinks } from 'components/Conversations'
import { toastAtom } from 'state'
import { useSetRecoilState } from 'recoil'
import { COLORS } from 'app-constants'

const ReadOnlyLabel = styled(Typography)`
  color: #666666;
`

const ValueLink = styled.a`
  color: #0073e1;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const ReadOnlyKeyPair = ({
  name,
  value,
  href,
  icon,
  isDarkBackdrop,
  isCopyable,
}: {
  name: string
  value?: React.ReactNode
  href?: string
  icon?: React.ReactNode
  isDarkBackdrop?: boolean
  isCopyable?: boolean
}) => {
  const theme = useTheme()
  const setToast = useSetRecoilState(toastAtom)

  if (!value) {
    return null
  }

  let renderValue = () => {
    if (!href) {
      // we want to parse value into words and turn any words that are links into links
      return getTextWithLinks(value as string)
    }

    const cleanHref = /^https?:\/\//i.test(href) ? href : `http://${href}`

    return (
      <ValueLink href={cleanHref} target="_blank">
        {value}
      </ValueLink>
    )
  }

  const onCopyClick = () => {
    navigator.clipboard.writeText(value as string)
    setToast({
      title: 'Copied to clipboard',
      severity: 'success',
    })
  }

  return (
    <div style={{ paddingBottom: '8px' }}>
      <ReadOnlyLabel
        variant="pXS"
        style={{
          color: getColorForDarkMode(theme, {
            isDarkBackdrop,
          }),
        }}>
        {name}
      </ReadOnlyLabel>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginLeft: icon ? '12px' : 0,
        }}>
        {icon && <div style={{ marginTop: '8px' }}>{icon}</div>}
        <Typography
          variant="pS"
          sx={{
            whiteSpace: 'pre-line',
            color: getColorForDarkMode(theme, {
              isDarkBackdrop,
            }),
            margin: icon ? '8px 0 0 8px;' : '8px 0 0 0px;',
          }}>
          {renderValue()}
        </Typography>
        {isCopyable && (
          <IconButton
            onClick={onCopyClick}
            sx={{
              height: '24px',
              width: '24px',
              mt: 1,
              ml: 1,
            }}>
            <ContentCopyIcon
              sx={{
                height: '16px',
                width: '16px',
                fill: COLORS.GRAY_3,
              }}
            />
          </IconButton>
        )}
      </div>
    </div>
  )
}

export default ReadOnlyKeyPair
